import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api'; // Use the updated api service
import { formatDistanceToNow } from 'date-fns';
import therapistStyles from './styles/therapistStyles'; // Import new styles
import AlertBadges from './alerts/AlertBadges'; // Import AlertBadges component
import PendingInvitationsSection from './PendingInvitationsSection';
import InviteClientDialog from './InviteClientDialog';
// MUI Imports
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip'; // Import Chip for status display
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'; // Icon for review requested
import Tooltip from '@mui/material/Tooltip'; // For showing exact date on hover
import PersonIcon from '@mui/icons-material/Person'; // Icon for client
import AccessTimeIcon from '@mui/icons-material/AccessTime'; // Icon for time
import SearchIcon from '@mui/icons-material/Search'; // Icon for search
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Stack from '@mui/material/Stack';

// import './ClientListView.css'; // Optional: Add styling later

function ClientListView({ token, onClientSelect, onClientsFetched }) { // Added onClientsFetched
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // Don't persist search term for privacy reasons - always start fresh
    const [searchTerm, setSearchTerm] = useState('');
    // Invitation state — bumping invitationsRefresh re-fetches the list
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [invitationsRefresh, setInvitationsRefresh] = useState(0);

    // Honour ?invite=1 (set by the dashboard "Invite client" tile) so the
    // dialog opens automatically when the user lands on the clients view.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('invite') === '1') {
            setInviteDialogOpen(true);
            // Clean up the query so a refresh / back-nav doesn't reopen.
            params.delete('invite');
            const next = params.toString();
            navigate(
                { pathname: location.pathname, search: next ? `?${next}` : '', hash: location.hash },
                { replace: true }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);
    
    // Load sort preferences from localStorage or use defaults
    const [orderBy, setOrderBy] = useState(() => {
        const saved = localStorage.getItem('therapist_client_list_orderBy');
        return saved || 'name';
    });
    const [order, setOrder] = useState(() => {
        const saved = localStorage.getItem('therapist_client_list_order');
        return saved || 'asc';
    });

    // Ticket 2.8 — quick filters (multi-select within a group = OR; across
    // groups = AND) + pagination. Filtering is client-side over the already
    // fetched caseload.
    const [statusFilters, setStatusFilters] = useState([]); // 'review' | 'active'
    const [riskFilters, setRiskFilters] = useState([]);     // 'high' | 'medium' | 'low'
    const [alertsOnly, setAlertsOnly] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    const toggleInArray = (setter) => (value) =>
        setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    const toggleStatusFilter = toggleInArray(setStatusFilters);
    const toggleRiskFilter = toggleInArray(setRiskFilters);
    const activeFilterCount = statusFilters.length + riskFilters.length + (alertsOnly ? 1 : 0);
    const clearFilters = () => { setStatusFilters([]); setRiskFilters([]); setAlertsOnly(false); };

    const fetchClients = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            // Use the correct API function name from api.js
            const response = await api.getAssignedClients(token);
            const fetchedClients = response || [];
            setClients(fetchedClients); // API returns the array directly
            if (onClientsFetched) {
                onClientsFetched(fetchedClients); // Pass fetched clients up
            }
        } catch (err) {
            console.error("Error fetching assigned clients:", err);
            setError('Failed to fetch assigned clients.');
            if (err.response && err.response.status === 403) {
                setError('Access denied. You might not have therapist privileges.');
            }
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            fetchClients();
        } else {
            setError("Authentication token not found.");
            setLoading(false);
        }
    }, [fetchClients, token]);

    // Handle sorting
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        
        // Update state
        setOrder(newOrder);
        setOrderBy(property);
        
        // Save preferences to localStorage
        localStorage.setItem('therapist_client_list_orderBy', property);
        localStorage.setItem('therapist_client_list_order', newOrder);
    };

    // Sort comparator function
    const getComparator = (order, orderBy) => {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    };

    const descendingComparator = (a, b, orderBy) => {
        let aValue = a[orderBy];
        let bValue = b[orderBy];

        // Handle special cases
        if (orderBy === 'lastActivity') {
            aValue = aValue ? new Date(aValue) : new Date(0);
            bValue = bValue ? new Date(bValue) : new Date(0);
        } else if (orderBy === 'date') {
            aValue = new Date(aValue);
            bValue = new Date(bValue);
        } else if (orderBy === 'status') {
            aValue = a.latestConversationStatus === 'human_review_requested' ? 1 : 0;
            bValue = b.latestConversationStatus === 'human_review_requested' ? 1 : 0;
        } else if (orderBy === 'alerts') {
            // Sort by conversation alerts first (pending count + priority), then risk level
            const getAlertPriority = (client) => {
                // Conversation alerts take highest priority
                if (client.alerts?.hasPendingAlerts) {
                    // Includes critical, which used to weigh 0 (PD-01).
                    const priorityWeight = {
                        critical: 2000,
                        urgent: 1000,
                        high: 500,
                        medium: 100,
                        low: 50
                    };
                    const weight = priorityWeight[client.alerts.highestPriority] || 0;
                    return weight + client.alerts.pendingCount;
                }

                // Fall back to triage risk level
                if (!client.triage || client.triage.status !== 'assessed') return 0;
                if (client.triage.riskLevel === 'high') return 3;
                if (client.triage.riskLevel === 'medium') return 2;
                if (client.triage.riskLevel === 'low') return 1;
                return 0;
            };
            aValue = getAlertPriority(a);
            bValue = getAlertPriority(b);
        }

        if (bValue < aValue) {
            return -1;
        }
        if (bValue > aValue) {
            return 1;
        }
        return 0;
    };

    // Filter and sort clients
    const filteredAndSortedClients = useMemo(() => {
        let filtered = clients;

        // Apply search filter
        if (searchTerm) {
            filtered = filtered.filter(client =>
                client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                client.email?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Status quick-filter (OR within group)
        if (statusFilters.length) {
            filtered = filtered.filter(client => {
                const isReview = client.latestConversationStatus === 'human_review_requested';
                return (statusFilters.includes('review') && isReview) ||
                       (statusFilters.includes('active') && !isReview);
            });
        }

        // Risk-level quick-filter (OR within group)
        if (riskFilters.length) {
            filtered = filtered.filter(client =>
                client.triage?.status === 'assessed' && riskFilters.includes(client.triage.riskLevel)
            );
        }

        // Pending-alerts quick-filter
        if (alertsOnly) {
            filtered = filtered.filter(client => client.alerts?.hasPendingAlerts);
        }

        // Apply sorting (copy first — sort mutates, and `clients` may be the source array)
        return [...filtered].sort(getComparator(order, orderBy));
    }, [clients, searchTerm, statusFilters, riskFilters, alertsOnly, order, orderBy]);

    // Reset to the first page whenever the result set changes.
    useEffect(() => {
        setPage(0);
    }, [searchTerm, statusFilters, riskFilters, alertsOnly, order, orderBy]);

    const pagedClients = useMemo(
        () => filteredAndSortedClients.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filteredAndSortedClients, page, rowsPerPage]
    );

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
    }

    if (error) {
        return <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>;
    }

    return (
        <Box>
            <Box sx={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'stretch' : 'center', 
                gap: isMobile ? 2 : 0,
                mb: 3 
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={therapistStyles.sectionHeader}>
                        My Assigned Clients
                    </Typography>
                    {clients.length > 0 && (
                        <Chip 
                            label={searchTerm 
                                ? `${filteredAndSortedClients.length} of ${clients.length} clients`
                                : `${clients.length} clients`
                            }
                            size="small"
                            sx={{ backgroundColor: '#f3f4f6' }}
                        />
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{
                            width: isMobile ? '100%' : 280,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        onClick={() => setInviteDialogOpen(true)}
                    >
                        Invite client
                    </Button>
                </Box>
            </Box>

            {/* Ticket 2.8 — quick-filter chips. Single-click, multi-select. */}
            {clients.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, mr: 0.5 }}>
                        Filter:
                    </Typography>
                    <Chip
                        label="Review requested"
                        size="small"
                        icon={<PriorityHighIcon />}
                        color={statusFilters.includes('review') ? 'warning' : 'default'}
                        variant={statusFilters.includes('review') ? 'filled' : 'outlined'}
                        onClick={() => toggleStatusFilter('review')}
                    />
                    <Chip
                        label="Active"
                        size="small"
                        color={statusFilters.includes('active') ? 'success' : 'default'}
                        variant={statusFilters.includes('active') ? 'filled' : 'outlined'}
                        onClick={() => toggleStatusFilter('active')}
                    />
                    <Box sx={{ width: '1px', height: 20, backgroundColor: '#e2e8f0', mx: 0.5 }} />
                    {['high', 'medium', 'low'].map((level) => (
                        <Chip
                            key={level}
                            label={`${level.charAt(0).toUpperCase()}${level.slice(1)} risk`}
                            size="small"
                            color={riskFilters.includes(level)
                                ? (level === 'high' ? 'error' : level === 'medium' ? 'warning' : 'info')
                                : 'default'}
                            variant={riskFilters.includes(level) ? 'filled' : 'outlined'}
                            onClick={() => toggleRiskFilter(level)}
                        />
                    ))}
                    <Box sx={{ width: '1px', height: 20, backgroundColor: '#e2e8f0', mx: 0.5 }} />
                    <Chip
                        label="Has alerts"
                        size="small"
                        color={alertsOnly ? 'error' : 'default'}
                        variant={alertsOnly ? 'filled' : 'outlined'}
                        onClick={() => setAlertsOnly((v) => !v)}
                    />
                    {activeFilterCount > 0 && (
                        <Button size="small" onClick={clearFilters} sx={{ textTransform: 'none', ml: 0.5 }}>
                            Clear ({activeFilterCount})
                        </Button>
                    )}
                </Box>
            )}

            {/* Pending invitations the therapist has created but the client
                hasn't accepted yet. Hidden when there are none. */}
            <PendingInvitationsSection token={token} refreshKey={invitationsRefresh} />

            <InviteClientDialog
                open={inviteDialogOpen}
                onClose={() => setInviteDialogOpen(false)}
                token={token}
                onCreated={() => setInvitationsRefresh(n => n + 1)}
            />
            
            {clients.length === 0 ? (
                <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    No clients are currently assigned to you.
                </Typography>
            ) : filteredAndSortedClients.length === 0 ? (
                <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    No clients match your search or filters.
                </Typography>
            ) : isMobile ? (
                // Mobile/Tablet Card Layout
                <Stack spacing={2}>
                    {pagedClients.map((client) => (
                        <Card key={client._id} sx={{ ...therapistStyles.tableContainer, p: 0 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PersonIcon sx={{ color: '#64748b', fontSize: '1.2rem' }} />
                                        <Typography sx={{ fontWeight: 500 }}>
                                            {client.name}
                                        </Typography>
                                    </Box>
                                    {client.latestConversationStatus === 'human_review_requested' && (
                                        <Chip
                                            icon={<PriorityHighIcon />}
                                            label="Review"
                                            size="small"
                                            sx={{
                                                ...therapistStyles.statusChip,
                                                ...therapistStyles.reviewStatus
                                            }}
                                        />
                                    )}
                                </Box>
                                
                                <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                                    {client.email}
                                </Typography>
                                
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                                        Joined: {new Date(client.date).toLocaleDateString()}
                                    </Typography>
                                    {client.lastActivity && (
                                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                                            Active: {formatDistanceToNow(new Date(client.lastActivity), { addSuffix: true })}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Alerts Section for Mobile */}
                                {(client.triage?.status === 'assessed' || client.alerts?.hasPendingAlerts) && (
                                    <Box sx={{ mt: 1 }}>
                                        <AlertBadges
                                            triage={client.triage}
                                            alerts={client.alerts}
                                            size="small"
                                            maxItems={2}
                                            showRiskLevel={true}
                                        />
                                    </Box>
                                )}
                            </CardContent>
                            <CardActions sx={{ px: 2, pb: 2 }}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={() => onClientSelect(client)}
                                    sx={{
                                        ...therapistStyles.primaryButton,
                                        py: 1
                                    }}
                                >
                                    View Client
                                </Button>
                            </CardActions>
                        </Card>
                    ))}
                </Stack>
            ) : (
                // Desktop Table Layout
                <TableContainer component={Paper} sx={therapistStyles.tableContainer}>
                    <Table aria-label="assigned clients table">
                        <TableHead sx={therapistStyles.tableHeader}>
                            <TableRow>
                                <TableCell sortDirection={orderBy === 'name' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'name'}
                                        direction={orderBy === 'name' ? order : 'asc'}
                                        onClick={() => handleRequestSort('name')}
                                    >
                                        Name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'email' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'email'}
                                        direction={orderBy === 'email' ? order : 'asc'}
                                        onClick={() => handleRequestSort('email')}
                                    >
                                        Email
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'date' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'date'}
                                        direction={orderBy === 'date' ? order : 'asc'}
                                        onClick={() => handleRequestSort('date')}
                                    >
                                        Joined Date
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'lastActivity' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'lastActivity'}
                                        direction={orderBy === 'lastActivity' ? order : 'asc'}
                                        onClick={() => handleRequestSort('lastActivity')}
                                    >
                                        Last Active
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'status' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'status'}
                                        direction={orderBy === 'status' ? order : 'asc'}
                                        onClick={() => handleRequestSort('status')}
                                    >
                                        Status
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'alerts' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'alerts'}
                                        direction={orderBy === 'alerts' ? order : 'asc'}
                                        onClick={() => handleRequestSort('alerts')}
                                    >
                                        Alerts
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: 88, position: 'sticky', right: 0, backgroundColor: 'background.paper', zIndex: 1 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pagedClients.map((client) => (
                                <TableRow
                                    key={client._id}
                                    hover
                                    onClick={() => onClientSelect(client)}
                                    sx={{ ...therapistStyles.tableRow, cursor: 'pointer' }}
                                >
                                    <TableCell component="th" scope="row">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PersonIcon sx={{ color: '#64748b', fontSize: '1.2rem' }} />
                                            <Typography sx={{ fontWeight: 500 }}>
                                                {client.name}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 220 }}>
                                        <Tooltip title={client.email || ''}>
                                            <Typography variant="body2" noWrap sx={{ color: '#64748b' }}>
                                                {client.email}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <Typography variant="body2">
                                            {new Date(client.date).toLocaleDateString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {client.lastActivity ? (
                                            <Tooltip title={new Date(client.lastActivity).toLocaleString('en-US', { 
                                                month: 'short', 
                                                day: 'numeric', 
                                                year: 'numeric',
                                                hour: 'numeric',
                                                minute: '2-digit',
                                                hour12: true
                                            })}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <AccessTimeIcon sx={{ fontSize: '0.9rem', color: '#94a3b8' }} />
                                                    <Typography variant="body2">
                                                        {formatDistanceToNow(new Date(client.lastActivity), { addSuffix: true })}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                                                Never
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {client.latestConversationStatus === 'human_review_requested' ? (
                                            <Chip
                                                icon={<PriorityHighIcon />}
                                                label="Review Requested"
                                                size="small"
                                                sx={{
                                                    ...therapistStyles.statusChip,
                                                    ...therapistStyles.reviewStatus
                                                }}
                                            />
                                        ) : (
                                            <Chip
                                                label="Active"
                                                size="small"
                                                sx={{
                                                    ...therapistStyles.statusChip,
                                                    ...therapistStyles.activeStatus
                                                }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <AlertBadges
                                            triage={client.triage}
                                            alerts={client.alerts}
                                            size="small"
                                            maxItems={3}
                                            showRiskLevel={true}
                                        />
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            whiteSpace: 'nowrap',
                                            // Keep the action reachable even if the table is wider than
                                            // the content area: pin it to the right edge of the scroll box.
                                            position: 'sticky',
                                            right: 0,
                                            backgroundColor: 'background.paper',
                                        }}
                                    >
                                        {/* Ticket 1.6 — icon action (the whole row is also clickable);
                                            stopPropagation so it doesn't double-fire with the row onClick. */}
                                        <Tooltip title={`View ${client.name}`}>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                aria-label={`View client ${client.name}`}
                                                onClick={(e) => { e.stopPropagation(); onClientSelect(client); }}
                                            >
                                                <ArrowForwardIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Ticket 2.8 — pagination (50/page default). Shown whenever there
                are results to page through. */}
            {filteredAndSortedClients.length > 0 && (
                <TablePagination
                    component="div"
                    count={filteredAndSortedClients.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[25, 50, 100]}
                    labelRowsPerPage="Clients per page"
                />
            )}
        </Box>
    );
};

export default ClientListView;
